import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { map, mergeMap, catchError, concatMap, takeLast } from 'rxjs/operators';
import { LinkSubjectToTeamContainerModel, LinkSubjectToTeamService, TeamService, TenantRoleService } from 'blg-akaun-ts-lib';
import { TeamPermActions } from '../actions';
import { ToastrService } from 'ngx-toastr';
import { AppConfig } from '../../../../visa';

@Injectable()
export class TeamPermEffects {

    private readonly apiVisa = AppConfig.apiVisa;

    /**
     * Update role details.
     */
    updateRole$ = createEffect(() => this.actions$.pipe(
        ofType(TeamPermActions.updateRoleInit),
        concatMap(action => this.tenantRoleService.getByGuid(action.guid, this.apiVisa)
        .pipe(
            map(a => a.data),
            map(b => {
                b.app_mst_role.name = action.role.value.name;
                b.app_mst_role.status = action.role.value.status;

                return b;
            })
        )),
        concatMap(c => this.tenantRoleService.put(c, this.apiVisa).pipe(
            map(_ => {
                this.displayToasterSuccess('Role updated successfully');
                return TeamPermActions.updateRoleSuccess();
            }),
            catchError(err => {
                this.displayToasterError(err.message);
                return of(TeamPermActions.updateRoleFailure({error: err.message}));
            })
        ))
    ))

    /**
     * Delete role.
     */
    deleteRole$ = createEffect(() => this.actions$.pipe(
        ofType(TeamPermActions.deleteRoleInit),
        mergeMap(action => this.tenantRoleService.delete(action.guid, this.apiVisa)
        .pipe(
            map(() => {
                this.displayToasterSuccess('Role deleted successfully')
                return TeamPermActions.deleteRoleSuccess();
            }),
            catchError((err) => {
                this.displayToasterError(err.message);
                return of(TeamPermActions.deleteRoleFailure({error: err.message}));
            })
        ))
    ))

    /**
     * Update team details.
     */
    updateTeam$ = createEffect(() => this.actions$.pipe(
        ofType(TeamPermActions.updateTeamInit),
        concatMap(action => this.teamService.getByGuid(action.guid, this.apiVisa)
        .pipe(
            map(a => a.data),
            map(b => {
                b.app_mst_grp_hdr.name = action.team.value.name;
                b.app_mst_grp_hdr.description = action.team.value.description;

                return b;
            })
        )),
        concatMap(c => this.teamService.put(c, this.apiVisa).pipe(
            map(_ => {
                this.displayToasterSuccess('Team updated successfully');
                return TeamPermActions.updateTeamSuccess();
            }),
            catchError(err => {
                this.displayToasterError(err.message);
                return of(TeamPermActions.updateTeamFailure({error: err.message}));
            })
        )),
        catchError(err => {
            this.displayToasterError(err.message);
            return of(TeamPermActions.updateTeamFailure({error: err.message}));
        })
    ))

    /**
     * Add members to the team.
     */
    addMembers$ = createEffect(() => this.actions$.pipe(
        ofType(TeamPermActions.addMembersInit),
        concatMap(action => {
            const arr: LinkSubjectToTeamContainerModel[] = [];
            action.members.forEach(memberGuid => {
                const a = new LinkSubjectToTeamContainerModel();
                a.link_subject_to_grp.app_grp_guid = action.teamGuid;
                a.link_subject_to_grp.app_subject_guid = memberGuid;
                a.link_subject_to_grp.rank = action.rank;
                a.link_subject_to_grp.title = action.rank;

                arr.push(a)
            })

            return arr
        }),
        mergeMap(obj => this.linkSubjectToTeamService.post(obj, this.apiVisa)
        .pipe(
            takeLast(1),
            map(_ => {
                this.displayToasterSuccess('Member added successfully');
                return TeamPermActions.addMembersSucess();
            }),
            catchError((err) => {
                this.displayToasterError(err.message);
                return of(TeamPermActions.addMembersFailure({error: err.message}))
            })
        ))
    ));

    /**
     * Update member details.
     */
     updateMember$ = createEffect(() => this.actions$.pipe(
        ofType(TeamPermActions.updateMemberInit),
        concatMap(action => this.linkSubjectToTeamService.getByGuid(action.guid, this.apiVisa)
        .pipe(
            map(a => a.data),
            map(b => {
                // update rank
                b.link_subject_to_grp.rank = action.member.value.rank;
                return b;
            })
        )),
        concatMap(c => this.linkSubjectToTeamService.put(c, this.apiVisa).pipe(
            map(_ => {
                this.displayToasterSuccess('Member updated successfully');
                return TeamPermActions.updateMemberSuccess();
            }),
            catchError(err => {
                this.displayToasterError(err.message);
                return of(TeamPermActions.updateMemberFailure({error: err.message}));
            })
        )),
        catchError(err => {
            this.displayToasterError(err.message);
            return of(TeamPermActions.updateMemberFailure({error: err.message}));
        })
    ))

    deleteMember$ = createEffect(() => this.actions$.pipe(
        ofType(TeamPermActions.deleteMemberInit),
        mergeMap(action => this.linkSubjectToTeamService.delete(action.guid, this.apiVisa)
        .pipe(
            map(() => {
                this.displayToasterSuccess('Member deleted successfully')
                return TeamPermActions.deleteMemberSuccess();
            }),
            catchError((err) => {
                this.displayToasterError(err.message);
                return of(TeamPermActions.deleteMemberFailure({error: err.message}));
            })
        ))
    ))

    constructor(
        private tenantRoleService: TenantRoleService,
        private teamService: TeamService,
        private linkSubjectToTeamService: LinkSubjectToTeamService,
        private actions$: Actions,
        private toaster: ToastrService
    ) {};

    /**
     * Display a successful toaster message.
     *
     * @param message : the message to display
     * @returns the toaster
     */
    displayToasterSuccess(message: string) {
        return this.toaster.success(
            message,
            'Success',
            {
                tapToDismiss: true,
                progressBar: true,
                timeOut: 1300
            }
        )
    }

    /**
     * Display a toaster when there is an error.
     *
     * @param message : the messsage to display
     * @returns the toaster
     */
    displayToasterError(message: string) {
        return this.toaster.error(
            message,
            'Error',
            {
                tapToDismiss: true,
                progressBar: true,
                timeOut: 1300
            }
        )
    }

}
